import { useHead } from '#imports'
import type { Ref } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import type { GetTechnicalSpecificationsResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/technical-specifications'
import type { Rating } from '@backmarket/http-api/src/api-specs-reviews/types/rating'

import type { DisplayedReview } from '~/scopes/reviews/reviews-display/models/reviews'
import { prepareJsonLdScript } from '~/utils/seo/schema'

import { useProductHeadDescription } from './useProductHeadDescription'
import { useProductHeadJsonLd } from './useProductHeadJsonLd'
import { useProductHeadLink } from './useProductHeadLink'
import { useProductHeadMeta } from './useProductHeadMeta'
import { useProductHeadTitle } from './useProductHeadTitle'

export function useProductHead(
  productResponse: Ref<GetProductResponse | undefined>,
  selectedOfferResponse: Ref<GetPickersResponse['selectedOffer']>,
  ratesResponse: Ref<Rating | undefined>,
  reviewsResponse: Ref<{ results: DisplayedReview[] } | undefined>,
  technicalSpecificationsResponse: Ref<
    GetTechnicalSpecificationsResponse | undefined
  >,
  isOutOfStock: Ref<boolean>,
) {
  const { title } = useProductHeadTitle({ productResponse })
  const { description } = useProductHeadDescription({ productResponse })
  const { meta } = useProductHeadMeta({ productResponse, title, description })
  const { link } = useProductHeadLink({ productResponse })
  const { jsonLd } = useProductHeadJsonLd({
    description,
    productResponse,
    selectedOfferResponse,
    ratesResponse,
    reviewsResponse,
    technicalSpecificationsResponse,
    isOutOfStock,
  })

  useHead({
    title,
    link,
    meta,
    script: jsonLd.value ? [prepareJsonLdScript(jsonLd.value)] : [],
  })
}
